/**
 * ImportsPanel — CSV import for drivers / customers / fixed-route fares.
 *
 * Phase 1 Slice 5 launch-readiness. Lets operators move existing
 * spreadsheets into Crystal Dispatch in minutes rather than typing every
 * record by hand.
 *
 * Design contract:
 *   - Pure organisational tooling. Reads + writes only via
 *     /admin/imports/{kind}/{template,preview,commit}. No dispatch,
 *     state-machine, janitor, offer-routing, ride-lifecycle, or payment
 *     code is touched.
 *   - Two-step UX: parse + preview (no writes) → commit. The Commit
 *     button stays disabled until at least one row is "ready".
 *   - Operators can drop a file OR paste CSV text. The textarea always
 *     reflects current state so they can fix a typo without re-uploading.
 *   - After commit, the panel surfaces the rollback flag prominently so
 *     a half-failed import never goes unnoticed.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Upload, Download, FileText, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Trash2, RefreshCw, Database, Users as UsersIcon, MapPin,
} from "lucide-react";

const KIND_META = {
  drivers: {
    label: "Drivers",
    icon: UsersIcon,
    intro: "Import your driver roster with vehicle and licence details. Drivers will be set up as approved + email-verified; you can suspend any of them from Fleet later.",
    templateName: "crystal-dispatch-drivers-template.csv",
    columns: ["row", "name", "email", "phone", "vehicle_make", "vehicle_plate", "licence_number", "status"],
  },
  customers: {
    label: "Customers",
    icon: Database,
    intro: "Import your passenger / customer book. Account-type 'corporate' flags the user as an invoice customer for monthly billing.",
    templateName: "crystal-dispatch-customers-template.csv",
    columns: ["row", "name", "email", "phone", "notes", "account_type"],
  },
  "fixed-routes": {
    label: "Fixed routes",
    icon: MapPin,
    intro: "Import your fixed-price routes (airport runs, hospital trips, school runs) so they appear as one-tap booking options for passengers.",
    templateName: "crystal-dispatch-fixed-routes-template.csv",
    columns: ["row", "name", "pickup", "destination", "fixed_price"],
  },
};

const KINDS = Object.keys(KIND_META);

export const ImportsPanel = () => {
  const [kind, setKind] = useState("drivers");
  return (
    <div className="bg-white" data-testid="imports-panel">
      <div className="px-4 md:px-6 py-4 border-b border-border bg-[#FAFAF7]">
        <h2 className="font-heading text-2xl font-black tracking-tight" data-testid="imports-title">
          Bulk import
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Move your fleet, customers, and routes from spreadsheets into Crystal Dispatch in minutes.
          Download a template, fill it in, paste or upload — we preview every row before anything is saved.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border bg-white px-4 md:px-6 pt-2" data-testid="imports-kind-tabs">
        {KINDS.map((k) => {
          const M = KIND_META[k];
          const Icon = M.icon;
          const active = kind === k;
          return (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${active ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`imports-tab-${k}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {M.label}
            </button>
          );
        })}
      </div>

      <ImportKindArea key={kind} kind={kind} />
    </div>
  );
};

const ImportKindArea = ({ kind }) => {
  const meta = KIND_META[kind];
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState("skip");
  // Drivers-only flag: when checked, freshly-imported drivers land as
  // approval_status="pending" so they go through the existing
  // Approvals + Documents review queue before they can dispatch.
  // Customers + fixed-routes ignore this state.
  const [importAsPending, setImportAsPending] = useState(false);
  // Drivers-only AND only meaningful when importAsPending is true.
  // When set, each freshly-imported pending driver receives a
  // one-shot Resend email pointing them at /login to upload their
  // documents. Default ON because the whole reason an operator opts
  // into pending mode is to chase the activation funnel.
  const [sendInviteEmails, setSendInviteEmails] = useState(true);
  const fileInputRef = useRef(null);

  const downloadTemplate = useCallback(async () => {
    // The template endpoint returns text/csv with Content-Disposition;
    // we don't need to wrap it in our axios `api` instance — a plain
    // anchor click works fine and gives the operator a real browser save.
    try {
      // Use api so the bearer token is attached, then create a blob URL
      // for download (anchor click avoids browser navigating away).
      const res = await api.get(`/admin/imports/template/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = meta.templateName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(formatApiError(e) || "Template download failed");
    }
  }, [kind, meta.templateName]);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File is over 5 MB. Please split into smaller batches.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(String(reader.result || ""));
      setPreview(null);
      setCommitResult(null);
    };
    reader.readAsText(f);
  }, []);

  const runPreview = useCallback(async () => {
    if (!csvText.trim()) {
      toast.error("Paste or upload CSV content first.");
      return;
    }
    setBusy(true); setCommitResult(null);
    try {
      const res = await api.post(`/admin/imports/${kind}/preview`, {
        csv_text: csvText, has_header: true,
      });
      setPreview(res.data);
      const s = res.data.summary;
      if (res.data.parse_errors?.length) {
        toast.error(res.data.parse_errors[0].message);
      } else if (s.errors === 0 && s.ready > 0) {
        toast.success(`${s.ready} row${s.ready === 1 ? "" : "s"} ready to import.`);
      } else if (s.errors > 0) {
        toast.warning(`${s.errors} row${s.errors === 1 ? "" : "s"} need fixing.`);
      } else if (s.duplicates > 0 && s.ready === 0) {
        toast.info(`${s.duplicates} duplicate${s.duplicates === 1 ? "" : "s"} found, nothing new to import.`);
      } else {
        toast.info("No data rows detected.");
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Preview failed");
    } finally {
      setBusy(false);
    }
  }, [csvText, kind]);

  const runCommit = useCallback(async () => {
    if (!preview || (preview.summary.ready === 0 && preview.summary.duplicates === 0)) {
      toast.error("Nothing to commit. Run preview first.");
      return;
    }
    setCommitting(true);
    try {
      const res = await api.post(`/admin/imports/${kind}/commit`, {
        csv_text: csvText, has_header: true, on_duplicate: onDuplicate,
        // Server ignores this for non-driver kinds; safe to always send.
        import_as_pending: kind === "drivers" ? importAsPending : false,
        // Server gates this on import_as_pending, but we explicitly
        // honour the dependent-disabled UX here: if the operator
        // didn't tick "pending", invites are unconditionally off.
        send_invite_emails: kind === "drivers" && importAsPending ? sendInviteEmails : false,
      });
      setCommitResult(res.data);
      if (res.data.rollback) {
        toast.error(`Import rolled back: ${res.data.error || "see report"}`);
      } else if (res.data.refused) {
        toast.error(`Import refused: ${res.data.reason || "duplicates present"}`);
      } else {
        const parts = [];
        if (res.data.committed) parts.push(`${res.data.committed} created`);
        if (res.data.updated) parts.push(`${res.data.updated} updated`);
        if (res.data.skipped) parts.push(`${res.data.skipped} skipped`);
        const pendingNote = kind === "drivers" && importAsPending && res.data.committed
          ? (sendInviteEmails
              ? ` · pending document review · ${res.data.committed} invite${res.data.committed === 1 ? "" : "s"} queued`
              : " · pending document review")
          : "";
        toast.success(`Import complete: ${parts.join(" · ") || "no changes"}${pendingNote}`);
        // Refresh preview to reflect new server-side duplicate state.
        setPreview(null);
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Commit failed");
    } finally {
      setCommitting(false);
    }
  }, [preview, csvText, kind, onDuplicate, importAsPending, sendInviteEmails]);

  const errorRows = useMemo(
    () => preview?.rows?.filter((r) => r.status === "error") || [],
    [preview],
  );

  const downloadErrorReport = useCallback(() => {
    if (!errorRows.length) return;
    const headers = ["row_number", "status", "errors", "raw_data"];
    const lines = [headers.join(",")];
    errorRows.forEach((r) => {
      lines.push([
        r.row_number, r.status,
        '"' + r.errors.join("; ").replace(/"/g, '""') + '"',
        '"' + JSON.stringify(r.raw).replace(/"/g, '""') + '"',
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `crystal-dispatch-${kind}-import-errors.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }, [errorRows, kind]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={`imports-area-${kind}`}>
      <p className="text-sm text-muted-foreground" data-testid={`imports-intro-${kind}`}>{meta.intro}</p>

      {/* Toolbar: template / upload / paste hint ──────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border border-border p-3 bg-[#FAFAF7]">
        <Button
          onClick={downloadTemplate} variant="outline" size="sm"
          className="flex items-center gap-1.5 text-xs"
          data-testid={`imports-template-${kind}`}
        >
          <Download className="w-3.5 h-3.5" /> Download CSV template
        </Button>
        <input
          ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFileChange}
          className="hidden" data-testid={`imports-file-${kind}`}
        />
        <Button
          onClick={() => fileInputRef.current?.click()} variant="outline" size="sm"
          className="flex items-center gap-1.5 text-xs"
          data-testid={`imports-upload-${kind}`}
        >
          <Upload className="w-3.5 h-3.5" /> Upload CSV file
        </Button>
        <span className="text-[11px] text-muted-foreground ml-2">
          Or paste CSV text directly into the editor below.
        </span>
      </div>

      {/* Editor / paste area ──────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="label-mini text-muted-foreground">CSV CONTENT</div>
          {csvText && (
            <button
              type="button"
              onClick={() => { setCsvText(""); setPreview(null); setCommitResult(null); }}
              className="text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
              data-testid={`imports-clear-${kind}`}
            >
              <Trash2 className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <textarea
          value={csvText} onChange={(e) => { setCsvText(e.target.value); setPreview(null); setCommitResult(null); }}
          rows={8} placeholder="name,email,phone,..."
          className="w-full border border-border p-3 text-xs font-mono-tab focus:outline-none focus:border-[#0A0A0A] resize-y"
          data-testid={`imports-textarea-${kind}`}
        />
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button
            onClick={runPreview} disabled={busy || !csvText.trim()}
            size="sm" className="bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center gap-1.5"
            data-testid={`imports-preview-${kind}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Preview
          </Button>
          {preview && (
            <Button
              onClick={() => { setPreview(null); setCommitResult(null); }}
              variant="ghost" size="sm" className="text-xs"
              data-testid={`imports-reset-${kind}`}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset preview
            </Button>
          )}
        </div>
      </div>

      {/* Preview report ───────────────────────────────────────────── */}
      {preview && <PreviewReport
        meta={meta} preview={preview} errorRows={errorRows}
        onDuplicate={onDuplicate} setOnDuplicate={setOnDuplicate}
        importAsPending={importAsPending} setImportAsPending={setImportAsPending}
        sendInviteEmails={sendInviteEmails} setSendInviteEmails={setSendInviteEmails}
        onCommit={runCommit} committing={committing}
        downloadErrorReport={downloadErrorReport} kind={kind}
      />}

      {/* Commit result ────────────────────────────────────────────── */}
      {commitResult && <CommitResult result={commitResult} kind={kind} />}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const cls = status === "ready"
    ? "bg-[#ECFDF5] text-[#065F46] border-[#10B981]"
    : status === "duplicate"
      ? "bg-[#FFFBEB] text-[#92400E] border-[#F59E0B]"
      : "bg-[#FEF2F2] text-[#7F1D1D] border-[#EF4444]";
  const Icon = status === "ready" ? CheckCircle2 : status === "duplicate" ? AlertTriangle : XCircle;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border font-bold uppercase ${cls}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const PreviewReport = ({ meta, preview, errorRows, onDuplicate, setOnDuplicate, importAsPending, setImportAsPending, sendInviteEmails, setSendInviteEmails, onCommit, committing, downloadErrorReport, kind }) => {
  const s = preview.summary;
  return (
    <div className="space-y-3" data-testid={`imports-preview-report-${kind}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryTile label="Total rows" value={s.total} testid={`summary-total-${kind}`} />
        <SummaryTile label="Ready" value={s.ready} testid={`summary-ready-${kind}`} tone="good" />
        <SummaryTile label="Duplicates" value={s.duplicates} testid={`summary-dupes-${kind}`} tone="warn" />
        <SummaryTile label="Errors" value={s.errors} testid={`summary-errors-${kind}`} tone="bad" />
      </div>

      {preview.parse_errors?.length > 0 && (
        <div className="border border-[#EF4444] bg-[#FEF2F2] text-[#7F1D1D] p-3 text-xs space-y-1" data-testid={`parse-errors-${kind}`}>
          {preview.parse_errors.map((e, i) => <div key={i}><strong>{e.kind}:</strong> {e.message}</div>)}
        </div>
      )}

      {/* Row-by-row table */}
      {preview.rows.length > 0 && (
        <div className="border border-border overflow-hidden">
          <div className="max-h-[400px] overflow-auto">
            <table className="w-full text-xs" data-testid={`imports-rows-${kind}`}>
              <thead className="bg-[#FAFAF7] sticky top-0 border-b border-border">
                <tr>
                  {meta.columns.map((c) => (
                    <th key={c} className="text-left px-2 py-1.5 font-bold uppercase tracking-wide text-[10px] text-muted-foreground">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => {
                  const data = r.data || r.raw;
                  return (
                    <tr key={r.row_number} className="border-b border-border last:border-b-0 hover:bg-secondary/50" data-testid={`row-${kind}-${r.row_number}`}>
                      <td className="px-2 py-1.5 font-mono-tab align-top">{r.row_number}</td>
                      {meta.columns.slice(1).map((c) => (
                        <td key={c} className="px-2 py-1.5 align-top">
                          {c === "row" ? null : (
                            <div className="truncate max-w-[200px]" title={String(data?.[c] ?? "")}>
                              {renderCell(data, c)}
                            </div>
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 align-top">
                        <StatusPill status={r.status} />
                        {r.errors?.length > 0 && (
                          <div className="text-[10px] text-[#7F1D1D] mt-0.5" data-testid={`row-${kind}-${r.row_number}-errors`}>
                            {r.errors.join("; ")}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commit controls */}
      <div className="flex flex-wrap items-center gap-3 border border-border p-3 bg-[#FAFAF7]">
        <label className="flex items-center gap-2 text-xs">
          <span className="label-mini text-muted-foreground">DUPLICATES</span>
          <select
            value={onDuplicate} onChange={(e) => setOnDuplicate(e.target.value)}
            className="border border-border px-2 py-1.5 text-xs bg-white"
            data-testid={`imports-on-duplicate-${kind}`}
          >
            <option value="skip">Skip — keep existing</option>
            <option value="update">Update existing</option>
            <option value="error">Refuse if any duplicates</option>
          </select>
        </label>

        {kind === "drivers" && (
          <label
            className="flex items-center gap-2 text-xs cursor-pointer border border-border bg-white px-3 py-1.5"
            data-testid="imports-pending-wrapper-drivers"
            title="If on, freshly-imported drivers land in the Approvals queue. They can sign in to upload documents but cannot go online until an admin reviews them."
          >
            <input
              type="checkbox"
              checked={importAsPending}
              onChange={(e) => setImportAsPending(e.target.checked)}
              data-testid="imports-pending-drivers"
              aria-describedby="imports-pending-help"
            />
            <span>
              Import as <strong>pending</strong> — require document review
            </span>
          </label>
        )}

        {kind === "drivers" && (
          <label
            className={`flex items-center gap-2 text-xs border bg-white px-3 py-1.5 ${importAsPending ? "border-border cursor-pointer" : "border-border/40 opacity-50 cursor-not-allowed"}`}
            data-testid="imports-send-invites-wrapper-drivers"
            title={importAsPending
              ? "Send a one-time Resend email to each imported pending driver with a CTA to log in and upload documents."
              : "Available only when 'Import as pending' is checked."}
          >
            <input
              type="checkbox"
              checked={importAsPending && sendInviteEmails}
              disabled={!importAsPending}
              onChange={(e) => setSendInviteEmails(e.target.checked)}
              data-testid="imports-send-invites-drivers"
              aria-describedby="imports-pending-help"
            />
            <span>
              Send <strong>document upload</strong> invitation email
            </span>
          </label>
        )}

        <Button
          onClick={onCommit}
          disabled={committing || (s.ready === 0 && (onDuplicate === "skip" || s.duplicates === 0))}
          size="sm" className="bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center gap-1.5"
          data-testid={`imports-commit-${kind}`}
        >
          {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Commit import
        </Button>
        {errorRows.length > 0 && (
          <Button
            onClick={downloadErrorReport} variant="outline" size="sm"
            className="text-xs flex items-center gap-1.5"
            data-testid={`imports-error-report-${kind}`}
          >
            <Download className="w-3.5 h-3.5" /> Download error report
          </Button>
        )}
      </div>
      {kind === "drivers" && importAsPending && (
        <div
          id="imports-pending-help"
          className="text-[11px] text-[#92400E] -mt-1 px-1 flex items-start gap-1.5"
          data-testid="imports-pending-help-drivers"
        >
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            Imported drivers will appear in <strong>Approvals</strong> with documents required.
            {sendInviteEmails
              ? " Each driver will receive an email pointing them at the login page to upload."
              : " You'll need to communicate the credentials to them yourself."}
            They won't receive ride offers until you approve them.
          </span>
        </div>
      )}
    </div>
  );
};

const renderCell = (data, key) => {
  if (!data) return <span className="text-muted-foreground">—</span>;
  const v = data[key];
  if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return v.toFixed(2);
  return String(v);
};

const SummaryTile = ({ label, value, testid, tone }) => {
  const cls = tone === "good" ? "border-[#10B981] bg-[#ECFDF5] text-[#065F46]"
    : tone === "warn" ? "border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]"
    : tone === "bad"  ? "border-[#EF4444] bg-[#FEF2F2] text-[#7F1D1D]"
    : "border-border bg-white";
  return (
    <div className={`border p-3 ${cls}`} data-testid={`tile-${testid}`}>
      <div className="label-mini opacity-70">{label}</div>
      <div className="text-2xl font-heading font-black mt-0.5 font-mono-tab" data-testid={`tile-${testid}-value`}>{value}</div>
    </div>
  );
};

const CommitResult = ({ result, kind }) => {
  if (result.rollback) {
    return (
      <div className="border border-[#EF4444] bg-[#FEF2F2] p-4 space-y-2" data-testid={`commit-result-${kind}`}>
        <div className="font-bold text-[#7F1D1D] flex items-center gap-2">
          <XCircle className="w-4 h-4" /> Import rolled back
        </div>
        <div className="text-xs text-[#7F1D1D]">
          A database error occurred mid-import. {result.rollback_count || 0} change(s) were reverted.
          Original error: {result.error}
        </div>
      </div>
    );
  }
  if (result.refused) {
    return (
      <div className="border border-[#F59E0B] bg-[#FFFBEB] p-4 space-y-1" data-testid={`commit-result-${kind}`}>
        <div className="font-bold text-[#92400E] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Import refused
        </div>
        <div className="text-xs text-[#92400E]">Reason: <strong>{result.reason}</strong>. Adjust the duplicates policy and re-commit.</div>
      </div>
    );
  }
  return (
    <div className="border border-[#10B981] bg-[#ECFDF5] p-4 space-y-1" data-testid={`commit-result-${kind}`}>
      <div className="font-bold text-[#065F46] flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> Import complete
      </div>
      <div className="text-xs text-[#065F46] space-x-3">
        <span data-testid={`commit-created-${kind}`}><strong>{result.committed}</strong> created</span>
        <span data-testid={`commit-updated-${kind}`}><strong>{result.updated}</strong> updated</span>
        <span data-testid={`commit-skipped-${kind}`}><strong>{result.skipped}</strong> skipped</span>
        <span data-testid={`commit-errored-${kind}`}><strong>{result.errored}</strong> errored</span>
      </div>
      <div className="text-[11px] text-[#065F46]/80 pt-1">
        {kind === "fixed-routes"
          ? <Link to="/admin" state={{ tab: "settings", sub: "routes" }} className="underline">View routes →</Link>
          : <Link to="/admin" state={{ tab: "fleet", sub: kind === "drivers" ? "drivers" : "passengers" }} className="underline">View in Fleet →</Link>}
      </div>
    </div>
  );
};

export default ImportsPanel;
